import apiClient from './api';

const API_URL = '/shifts';

export interface Shift {
    id: string;
    userId: string;
    startTime: string;
    endTime: string;
    breakDuration?: number;
    type: string;
    location?: string;
    status: string;
    isPublished: boolean;
    notes?: string;
    versionId?: string;
    originalUserId?: string;
    needsCover?: boolean;
    coverReason?: string;
    coverSourceLeaveRequestId?: string;
    assignmentChanged?: boolean;
    assignmentChangeType?: string;
    assignmentSourceSwapId?: string;
    user: {
        id: string;
        name: string;
        email: string;
        role?: {
            name: string;
        };
    };
    originalUser?: {
        id: string;
        name: string;
        email: string;
        role?: {
            name: string;
        };
    };
    version?: {
        id: string;
        versionNumber: number;
        status: string;
    };
}

export interface RosterVersion {
    id: string;
    versionNumber: number;
    startDate: string;
    endDate: string;
    publishedAt: string | null;
    publishedBy: string | null;
    status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
    notes: string | null;
    createdAt: string;
    updatedAt: string;
    shiftCount: number;
}

export type RosterPublicationStatus = 'PUBLISHED' | 'UNPUBLISHED' | 'NOT_CREATED';

export interface CoverageReport {
    date: string;
    coverage: Record<string, number>;
    alerts: string[];
    isSufficient: boolean;
    skillGaps: Array<{
        shiftType: string;
        required: number;
        current: number;
        gap: number;
    }>;
}

export interface PatternConflict {
    hasConflicts: boolean;
    conflicts: string[];
}

// Get calendar date range from server (avoids timezone issues)
export const getCalendarRange = async (year: number, month: number, view: 'month' | 'week' = 'month') => {
    const response = await apiClient.get(`${API_URL}/calendar/range`, {
        params: {
            year,
            month,
            view,
        },
    });
    return response.data;
};

// Shift Management
export const getShifts = async (start: Date, end: Date, userId?: string, versionId?: string) => {
    const response = await apiClient.get(API_URL, {
        params: {
            start: start.toISOString(),
            end: end.toISOString(),
            userId,
            versionId,
        },
    });
    return response.data;
};

export const getShiftById = async (id: string) => {
    const response = await apiClient.get(`${API_URL}/${id}`);
    return response.data;
};

export const createShift = async (data: {
    userId: string;
    startTime: string;
    endTime: string;
    type: string;
    location?: string;
    notes?: string;
    versionId?: string;
}) => {
    const response = await apiClient.post(API_URL, data);
    return response.data;
};

export const updateShift = async (id: string, data: {
    userId?: string;
    startTime?: string;
    endTime?: string;
    type?: string;
    location?: string;
    notes?: string;
    status?: string;
    versionId?: string;
}) => {
    const response = await apiClient.put(`${API_URL}/${id}`, data);
    return response.data;
};

export const deleteShift = async (id: string) => {
    const response = await apiClient.delete(`${API_URL}/${id}`);
    return response.data;
};

// Pattern Generation
export const generatePattern = async (data: {
    userId?: string;
    controller1Id?: string;
    controller2Id?: string;
    startDate: string;
    weeks?: number;
    type?: string;
    versionId?: string;
}) => {
    const response = await apiClient.post(`${API_URL}/generate-pattern`, data);
    return response.data;
};

export const detectPatternConflicts = async (data: {
    userId: string;
    startDate: string;
    weeks?: number;
}): Promise<PatternConflict> => {
    const response = await apiClient.post(`${API_URL}/pattern/conflicts`, data);
    return response.data;
};

// Playground Generation
export const generatePlaygroundRoster = async (data: {
    startDate: string;
    endDate: string;
    versionId?: string;
}) => {
    const response = await apiClient.post(`${API_URL}/playground/generate`, data);
    return response.data;
};

export const findControllers = async (): Promise<Array<{ id: string; name: string; email: string }>> => {
    try {
        const response = await apiClient.get(`${API_URL}/controllers/find`);
        // Handle different response formats
        if (Array.isArray(response.data)) {
            return response.data;
        }
        if (response.data?.data && Array.isArray(response.data.data)) {
            return response.data.data;
        }
        return [];
    } catch (error: any) {
        console.error('Error fetching controllers:', error);
        throw error;
    }
};

// Bulk Operations
export const bulkAssignShifts = async (data: {
    userIds: string[];
    startTime: string;
    endTime: string;
    type: string;
    location?: string;
    versionId?: string;
}) => {
    const response = await apiClient.post(`${API_URL}/bulk-assign`, data);
    return response.data;
};

// Coverage
export const checkCoverage = async (date: Date): Promise<CoverageReport> => {
    const response = await apiClient.get(`${API_URL}/coverage`, {
        params: { date: date.toISOString() },
    });
    return response.data;
};

export const getCoverageForRange = async (start: Date, end: Date): Promise<CoverageReport[]> => {
    const response = await apiClient.get(`${API_URL}/coverage`, {
        params: {
            start: start.toISOString(),
            end: end.toISOString(),
        },
    });
    return response.data;
};

export const detectCoverageGaps = async (date: Date) => {
    const response = await apiClient.get(`${API_URL}/coverage/gaps`, {
        params: { date: date.toISOString() },
    });
    return response.data;
};

// Roster Publishing
export const publishRoster = async (start: Date, end: Date) => {
    const response = await apiClient.post(`${API_URL}/publish`, {
        start: start.toISOString(),
        end: end.toISOString(),
    });
    return response.data;
};

// Version Management
export const getRosterVersions = async (): Promise<RosterVersion[]> => {
    const response = await apiClient.get(`${API_URL}/versions`);
    return response.data;
};

export const getRosterVersionById = async (id: string): Promise<RosterVersion> => {
    const response = await apiClient.get(`${API_URL}/versions/${id}`);
    return response.data;
};

export const createRosterVersion = async (data: {
    startDate: string;
    endDate: string;
    notes?: string;
}): Promise<RosterVersion> => {
    const response = await apiClient.post(`${API_URL}/versions`, data);
    return response.data;
};

export const updateRosterVersionNotes = async (id: string, notes: string): Promise<RosterVersion> => {
    const response = await apiClient.patch(`${API_URL}/versions/${id}/notes`, { notes });
    return response.data;
};

export const publishRosterVersion = async (id: string): Promise<RosterVersion> => {
    const response = await apiClient.post(`${API_URL}/versions/${id}/publish`, {});
    return response.data;
};

export const rollbackVersion = async (id: string): Promise<RosterVersion> => {
    const response = await apiClient.post(`${API_URL}/versions/${id}/rollback`, {});
    return response.data;
};

export const unpublishRosterVersion = async (id: string): Promise<RosterVersion> => {
    const response = await apiClient.post(`${API_URL}/versions/${id}/unpublish`, {});
    return response.data;
};

export const deleteVersion = async (id: string): Promise<void> => {
    await apiClient.delete(`${API_URL}/versions/${id}`);
};

export const compareVersions = async (versionId1: string, versionId2: string) => {
    const response = await apiClient.get(`${API_URL}/versions/compare`, {
        params: { versionId1, versionId2 },
    });
    return response.data;
};

/**
 * Get monthly roster in grid format
 */
export const getMonthlyRoster = async (year: number, month: number, versionId?: string) => {
    const response = await apiClient.get(`${API_URL}/monthly/${year}/${month}`, {
        params: { versionId }
    });
    return response.data.data;
};

export const getPublishedRoster = async (year: number, month: number) => {
    const response = await apiClient.get(`${API_URL}/published/${year}/${month}`);
    return response.data.data;
};

export const getRosterPublicationStatus = async (
    year: number,
    month: number
): Promise<RosterPublicationStatus> => {
    const response = await apiClient.get(`${API_URL}/publication-status/${year}/${month}`);
    return response.data.data.status;
};
